import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, Flame } from "lucide-react";
import { useCart } from "../context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-ink-800 border-l border-ink-600 text-cream w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-ink-600">
          <SheetTitle className="font-display text-3xl text-cream flex items-center gap-3">
            <Flame className="text-brand" size={24} />
            YOUR CART
            <span className="text-brand text-xl">({count})</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-20">
              <div className="font-display text-4xl text-cream">EMPTY PIT</div>
              <p className="text-[#A19D94] text-sm">Time to load up some smoked goodness.</p>
              <button
                onClick={() => { setIsOpen(false); navigate("/shop"); }}
                data-testid="cart-empty-shop-btn"
                className="mt-4 px-6 h-11 bg-brand text-ink-900 font-display text-lg tracking-wide hover:bg-brand-hover transition-all"
              >
                BROWSE THE PIT
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-4 border-b border-ink-600 pb-4" data-testid={`cart-item-${it.product_id}`}>
                  <img src={it.image_url} alt={it.name} className="w-20 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl text-cream leading-tight">{it.name}</div>
                    <div className="text-xs text-[#A19D94] tracking-wider mt-1">{it.unit}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-ink-600">
                        <button
                          data-testid={`cart-decrement-${it.product_id}`}
                          onClick={() => updateQuantity(it.product_id, Math.max(0, it.quantity - 1))}
                          className="w-8 h-8 flex items-center justify-center hover:bg-ink-700"
                        ><Minus size={14} /></button>
                        <span className="w-8 text-center font-display text-lg">{it.quantity}</span>
                        <button
                          data-testid={`cart-increment-${it.product_id}`}
                          onClick={() => updateQuantity(it.product_id, it.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-ink-700"
                        ><Plus size={14} /></button>
                      </div>
                      <div className="font-display text-xl text-brand">${(it.price * it.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-remove-${it.product_id}`}
                    onClick={() => removeItem(it.product_id)}
                    className="text-[#A19D94] hover:text-[#D9381E] self-start"
                  ><Trash2 size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-ink-600 p-6 space-y-4 bg-ink-900">
            <div className="flex justify-between items-end">
              <span className="font-display text-2xl text-cream tracking-wider">SUBTOTAL</span>
              <span className="font-display text-3xl text-brand">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-[#A19D94]">Pickup only at Gosford Showgrounds. Final pickup time chosen at checkout.</p>
            <button
              data-testid="cart-checkout-btn"
              onClick={handleCheckout}
              className="w-full h-14 bg-brand hover:bg-brand-hover text-ink-900 font-display text-2xl tracking-widest teal-glow-hover transition-all"
            >
              CHECKOUT
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
